import { useLayoutEffect, useRef, useState } from 'react';
import { useRenderPicto } from '@jooxter/ui';
import { BASE_FONT_SIZE, STROKE_WIDTH } from '../constants';
import { IJxtPictoGroup } from '../types';
import { JxtFavoriteBadge } from './JxtFavoriteBadge';

// padding of one side in px
const PADDING = 8;
const SPACING = 20;
const INTERLINE = 8;
// max chars we want to display for title and subtitle
const MAX_CHARACTERS = 40;

export const JxtResourcePicto = ({ p, onPictoClick, isHovered, computedRatio }: IJxtPictoGroup) => {
  const showTooltip = p.showMapIcon || isHovered;
  const groupRef = useRef<SVGGElement>(null);
  const titleRef = useRef<SVGTextElement>(null);
  const subtitleRef = useRef<SVGTextElement>(null);
  const textRef = useRef<SVGGElement>(null);
  const [titleWidth, setTitleWidth] = useState<number>();
  const { pictoSvg } = useRenderPicto({
    capacity: p.capacity,
    occupantsCount: p.occupantsCount,
    size: p.size,
    x: p.x,
    y: p.y,
    status: p.status,
    url: p.url,
  });
  const [subtitleWidth, setSubtitleWidth] = useState<number>();
  const [subtitleHeight, setSubtitleHeight] = useState<number>();
  const [baseHeight, setBaseHeight] = useState<number>();
  const baseWidth = titleWidth && (!subtitleWidth || titleWidth > subtitleWidth) ? titleWidth : subtitleWidth;
  const titleFontSize = computedRatio ? computedRatio * BASE_FONT_SIZE : BASE_FONT_SIZE;
  const tooltipClass = showTooltip ? 'visible' : 'invisible';
  const truncatedTitle =
    p.title && p.title?.length > MAX_CHARACTERS ? p.title.substring(0, MAX_CHARACTERS).concat('...') : p.title;
  const truncatedSubtitle =
    p.subtitle && p.subtitle?.length > MAX_CHARACTERS
      ? p.subtitle.substring(0, MAX_CHARACTERS).concat('...')
      : p.subtitle;

  // Measure after layout so the background follows the actual text size: the font size depends on
  // computedRatio, which is only known once the parent svg is mounted.
  useLayoutEffect(() => {
    const titleBox = titleRef.current?.getBBox();
    const subtitleBox = subtitleRef.current?.getBBox();
    setTitleWidth(titleBox?.width);
    setSubtitleWidth(subtitleBox?.width);
    setSubtitleHeight(subtitleBox?.height);
    setBaseHeight(textRef.current?.getBBox().height);
  }, [truncatedTitle, truncatedSubtitle, titleFontSize, subtitleHeight, p.x, p.y]);

  if (!(p.x && p.y)) {
    return null;
  }

  return (
    <g
      ref={groupRef}
      data-picto-id={p.id}
      onClick={() => onPictoClick(p.id)}
      onTouchEnd={() => onPictoClick(p.id)}
      className="cursor-pointer"
    >
      {/* Found by the plan to focus on it (JxtFloorPlan `focusPictoId`). */}
      <g data-picto-marker={p.id}>{pictoSvg()}</g>
      {p.isFavorite && <JxtFavoriteBadge x={p.x} y={p.y} size={p.size} />}
      {baseWidth && baseHeight && (
        <rect
          className={tooltipClass}
          width={baseWidth + PADDING * 2}
          height={baseHeight + PADDING}
          fill="white"
          stroke="#e0e3e8"
          strokeWidth={computedRatio ? computedRatio * STROKE_WIDTH : STROKE_WIDTH}
          rx="8"
          ry="8"
          x={p.x - baseWidth / 2 - 8 + p.size / 2 - 1}
          y={
            p.y - baseHeight / 2 - PADDING / 2 - (p.subtitle && subtitleHeight ? SPACING + subtitleHeight / 2 : SPACING)
          }
        />
      )}
      <g ref={textRef}>
        <text
          ref={titleRef}
          className={tooltipClass}
          textAnchor="middle"
          alignmentBaseline="central"
          x={p.x + p.size / 2}
          y={p.y - (p.subtitle && subtitleHeight ? SPACING + INTERLINE + subtitleHeight - PADDING : SPACING)}
          fontFamily='"Open Sans", sans-serif'
          fontSize={titleFontSize}
          fontWeight="800"
          fill="#22272e"
        >
          {truncatedTitle}
        </text>
        {p.subtitle && (
          <text
            ref={subtitleRef}
            className={tooltipClass}
            textAnchor="middle"
            alignmentBaseline="central"
            x={p.x + p.size / 2}
            y={p.y - SPACING}
            fontFamily='"Open Sans", sans-serif'
            fontSize={titleFontSize}
            fontWeight="400"
            fill="#677489"
          >
            {truncatedSubtitle}
          </text>
        )}
      </g>
    </g>
  );
};
